// src/components/utils/SoundManager.js
import pop1 from "../audio/Pop_1.mp3";
import pop2 from "../audio/Pop_2.mp3";
import pop3 from "../audio/Pop_3.mp3";
import bumpFile from '../audio/bump.mp3';
import gameoverFile from '../audio/gameover.mp3';
import collectFile from '../audio/collect.mp3';
import gridLevelUpFile from '../audio/gridlevelup.mp3';
import musicFile from '../audio/background_music.mp3';
import coinRainFile from '../audio/coinrain.mp3';

import coin1File from '../audio/coin1.mp3';
import coin2File from '../audio/coin2.mp3';
import coin3File from '../audio/coin3.mp3';
import coin4File from '../audio/coin4.mp3';
import coin5File from '../audio/coin5.mp3';
import ghostFile from '../audio/ghost.mp3';
import blingFile from '../audio/bling.mp3';

import uiHoverFile from '../audio/ui_hover.mp3';
import uiOpenFile from '../audio/ui_open.mp3';
import uiConfirmFile from '../audio/ui_confirm.mp3';
import uiCloseFile from '../audio/ui_close.mp3';

import click1File from '../audio/click1.mp3';
import click2File from '../audio/click2.mp3';
import equipFile from '../audio/equip.mp3';
import cashRegisterFile from '../audio/cashregister.mp3';
import uiPauseFile from '../audio/ui_pause.mp3';

class SoundManager {
    constructor() {
        this.context = null;
        this.masterGain = null;
        this.musicGain = null;
        this.sfxGain = null;
        
        this.buffers = {};
        this.musicSource = null;
        this.isMusicPlaying = false;
        this.isWarmedUp = false; 

        // Track mute states internally
        this.musicMuted = false;
        this.sfxMuted = false;
        
        this.soundUrls = {
            pop1, pop2, pop3,
            bump: bumpFile,
            gameover: gameoverFile,
            collect: collectFile,
            levelup: gridLevelUpFile,
            music: musicFile,
            coinrain: coinRainFile,
            coin1: coin1File,
            coin2: coin2File,
            coin3: coin3File,
            coin4: coin4File,
            coin5: coin5File,
            ghost: ghostFile,
            bling: blingFile,
            uiHover: uiHoverFile,
            uiOpen: uiOpenFile,
            uiConfirm: uiConfirmFile,
            uiClose: uiCloseFile,
            click1: click1File,
            click2: click2File,
            equip: equipFile,
            cashregister: cashRegisterFile,
            uiPause: uiPauseFile
        };
    }

    init() {
        if (this.context) return;

        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.context = new AudioContext();

        this.masterGain = this.context.createGain();
        this.masterGain.connect(this.context.destination);

        this.musicGain = this.context.createGain();
        this.musicGain.connect(this.masterGain);

        this.sfxGain = this.context.createGain();
        this.sfxGain.connect(this.masterGain);

        this.loadSounds();
    }

    async loadSounds() {
        const promises = Object.entries(this.soundUrls).map(async ([key, url]) => {
            try {
                const response = await fetch(url);
                const arrayBuffer = await response.arrayBuffer();
                if (this.context) {
                    const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
                    this.buffers[key] = audioBuffer;
                }
            } catch (e) {
                console.error(`Failed to load sound: ${key}`, e);
            }
        });
        await Promise.all(promises);
    }

    async resumeContext() {
        if (!this.context) return;

        if (!this.isWarmedUp) {
            const buffer = this.context.createBuffer(1, 1, 22050); 
            const source = this.context.createBufferSource();
            source.buffer = buffer;
            source.connect(this.context.destination);
            source.start(0);
            this.isWarmedUp = true;
        }

        // Fix: Check for 'interrupted' state (iOS specific) as well as 'suspended'
        if (this.context.state === 'suspended' || this.context.state === 'interrupted') {
            try {
                await this.context.resume();
            } catch (e) {
                console.error("Audio resume failed:", e);
            }
        }
    }
    
    isReady() {
        return this.context && this.context.state === 'running';
    }

    playSFX(key, volume = 1) {
        if (!this.context || !this.buffers[key]) return;
        
        // Fix: Attempt auto-resume if interrupted
        if (this.context.state === 'suspended' || this.context.state === 'interrupted') {
             this.context.resume().catch(e => console.error("Auto-resume failed", e));
        }

        const source = this.context.createBufferSource();
        source.buffer = this.buffers[key];
        
        const gain = this.context.createGain();
        gain.gain.value = volume;
        
        source.connect(gain);
        gain.connect(this.sfxGain);
        
        source.start(0);
    }

    playRandomPop() {
        const pops = ['pop1', 'pop2', 'pop3'];
        const randomPop = pops[Math.floor(Math.random() * pops.length)];
        this.playSFX(randomPop, 0.8); 
    }

    playRandomCoin() {
        const coins = ['coin1', 'coin2', 'coin3', 'coin4', 'coin5'];
        const randomCoin = coins[Math.floor(Math.random() * coins.length)];
        this.playSFX(randomCoin, 0.5); 
    }

    playMusic(fadeInDuration = 1) {
        if (!this.context || !this.buffers.music) return;
        if (this.isMusicPlaying) return;

        // Fix: Handle interrupted state here too
        if (this.context.state === 'suspended' || this.context.state === 'interrupted') {
             this.context.resume().catch(e => console.error("Music auto-resume failed", e));
        }

        this.musicSource = this.context.createBufferSource();
        this.musicSource.buffer = this.buffers.music;
        this.musicSource.loop = true;
        this.musicSource.connect(this.musicGain);
        
        this.musicSource.start(0);
        this.isMusicPlaying = true;

        this.musicGain.gain.cancelScheduledValues(this.context.currentTime);
        this.musicGain.gain.setValueAtTime(0, this.context.currentTime);
        
        // FIX: Check internal mute state before ramping up volume
        const targetVolume = this.musicMuted ? 0 : 0.5;
        this.musicGain.gain.linearRampToValueAtTime(targetVolume, this.context.currentTime + fadeInDuration);
    }

    stopMusic(fadeOutDuration = 1) {
        if (!this.context || !this.isMusicPlaying || !this.musicSource) return;

        this.musicGain.gain.cancelScheduledValues(this.context.currentTime);
        this.musicGain.gain.setValueAtTime(this.musicGain.gain.value, this.context.currentTime);
        this.musicGain.gain.linearRampToValueAtTime(0, this.context.currentTime + fadeOutDuration);

        setTimeout(() => {
            if (this.musicSource) {
                try {
                    this.musicSource.stop();
                    this.musicSource.disconnect();
                } catch (e) {}
                this.musicSource = null;
            }
            this.isMusicPlaying = false;
        }, fadeOutDuration * 1000);
    }

    setMusicMute(muted) {
        this.musicMuted = muted; // Update internal state
        if (!this.musicGain || !this.context) return;
        const target = muted ? 0 : 0.5;
        this.musicGain.gain.setTargetAtTime(target, this.context.currentTime, 0.1);
    }

    setSfxMute(muted) {
        this.sfxMuted = muted; // Update internal state
        if (!this.sfxGain || !this.context) return;
        const target = muted ? 0 : 1;
        this.sfxGain.gain.setTargetAtTime(target, this.context.currentTime, 0.1);
    }
}

export const soundManager = new SoundManager();